/**
 * Bridge between the v2 SSE canonical `FindingEvent.verdict` label produced
 * server-side and the four verdict filter keys the ValidationMode side-control
 * cluster has always exposed (`valid`, `invalid`, `undetermined`,
 * `not-applicable`). The legacy `.article > .conclusion` DOM pipeline exposed
 * these keys directly as CSS classes; the canonical `<FindingCard>` output
 * carries the raw server verdict (`pass`, `fail`, `undetermined`,
 * `not_applicable`, …), so we translate once here.
 *
 * Restoring the filter buttons to work over streamed findings (Sprint 3
 * story #224) requires the same key vocabulary; unknown verdicts collapse
 * to `unknown` and are only shown when no filter is active.
 */

export type VerdictFilterKey =
  | 'valid'
  | 'invalid'
  | 'undetermined'
  | 'not-applicable'
  | 'unknown';

const VERDICT_MAP: Record<string, VerdictFilterKey> = {
  pass: 'valid',
  passed: 'valid',
  valid: 'valid',
  compliant: 'valid',
  fail: 'invalid',
  failed: 'invalid',
  invalid: 'invalid',
  noncompliant: 'invalid',
  'non-compliant': 'invalid',
  undetermined: 'undetermined',
  indeterminate: 'undetermined',
  inconclusive: 'undetermined',
  not_applicable: 'not-applicable',
  'not-applicable': 'not-applicable',
  na: 'not-applicable',
  'n/a': 'not-applicable',
};

export function verdictFilterKey(verdict: string | undefined | null): VerdictFilterKey {
  if (!verdict) return 'unknown';
  const normalised = verdict.toString().trim().toLowerCase();
  return VERDICT_MAP[normalised] ?? 'unknown';
}
